/*
 * Size finder for the enhanced size guides (Theme settings > Size and fit).
 *
 * snippets/size-guide.liquid renders a <size-guide> element with a JSON config built from one size guide
 * metaobject (size_guide_clothing, _footwear, _ring, _bracelet or _chain). This file turns that config into
 * a short step-by-step finder inside Prestige's modal, recommends one of the product's own variant sizes and
 * can select it. Recommendations never invent a size: if the answer falls outside the sizes on offer, the
 * finder says so.
 */

const LETTER_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];

export const normalizeSize = value => String(value).trim().toUpperCase().replace(/^2XL$/, 'XXL').replace(/^3XL$/, 'XXXL');

/* Converts a variant label such as 7", 18 cm or 180mm to centimetres. Plain numbers are not guessed. */
export function lengthCm(value) {
  const match = String(value).trim().match(/^(\d+(?:[.,]\d+)?)\s*(cm|mm|inches|inch|in|["″])$/i);
  if (!match) return null;
  const number = Number(match[1].replace(',', '.'));
  const unit = match[2].toLowerCase();
  if (unit === 'mm') return number / 10;
  if (unit === 'cm') return number;
  return number * 2.54;
}

function clothingOrder(config) {
  const offered = config.sizes.map(size => size.label);
  const custom = config.guide.sizeOrder || [];
  if (custom.length) return custom;
  return offered.every(label => LETTER_SIZES.includes(normalizeSize(label))) ? LETTER_SIZES : offered;
}

/*
 * Returns {label} for a recommendation, or {} when no offered size matches.
 * answers holds what the customer picked: usual, fit, shoe, width, ring, wrist, choice.
 */
export function recommend(config, answers) {
  const guide = config.guide || {};
  const offered = config.sizes.map(size => size.label);
  const offeredLabel = value => offered.find(label => normalizeSize(label) === normalizeSize(value));
  const adjustment = Number(config.adjustment || 0);

  switch (config.mode) {
    case 'clothing': {
      const order = clothingOrder(config);
      const index = order.map(normalizeSize).indexOf(normalizeSize(answers.usual));
      const fitSteps = { tight: Number(guide.slimSteps || 0), regular: 0, loose: Number(guide.relaxedSteps ?? 1) }[answers.fit || 'regular'];
      if (index < 0 || !Number.isInteger(fitSteps) || !Number.isInteger(adjustment)) return {};
      const target = order[index + adjustment + fitSteps];
      return target ? { label: offeredLabel(target) } : {};
    }
    case 'footwear': {
      const table = Array.isArray(guide.table) ? guide.table : [];
      const system = guide.system;
      const row = table.findIndex(entry => String(entry[answers.shoeSystem]) === String(answers.shoe));
      const widthSteps = { narrow: Number(guide.narrowSteps || 0), regular: 0, wide: Number(guide.wideSteps || 0) }[answers.width || 'regular'];
      if (row < 0 || !system || !Number.isInteger(adjustment + widthSteps)) return {};
      const target = table[row + adjustment + widthSteps];
      return target ? { label: offeredLabel(target[system]) } : {};
    }
    case 'ring': {
      const sizes = guide.sizes || [];
      const diameters = (guide.diameters || []).map(Number);
      const tolerance = Number(guide.tolerance || 0.2);
      const measured = Number(answers.ring);
      if (!measured || sizes.length !== diameters.length) return {};
      const matches = sizes
        .map((size, i) => ({ size, distance: Math.abs(diameters[i] - measured) }))
        .filter(row => row.distance <= tolerance + 1e-9)
        .sort((a, b) => a.distance - b.distance);
      if (!matches.length || (matches[1] && Math.abs(matches[0].distance - matches[1].distance) < 1e-9)) return {};
      return { label: offeredLabel(matches[0].size) };
    }
    case 'bracelet': {
      const wrist = Number(answers.wrist);
      const allowance = Number(guide.allowance);
      if (!(wrist > 5 && wrist < 50) || !(allowance >= 0)) return {};
      const target = wrist + allowance;
      const options = offered.map(label => ({ label, cm: lengthCm(label) })).filter(o => o.cm !== null).sort((a, b) => a.cm - b.cm);
      const selected = options.find(o => o.cm + 0.001 >= target);
      if (!selected || selected.cm - target > Number(guide.maxExtra ?? 2)) return {};
      return { label: selected.label };
    }
    case 'chain':
      return { label: offeredLabel(answers.choice) };
    default:
      return {};
  }
}

/* Chain lengths from the guide that this product actually offers, with their fit descriptions. A chain sits higher
   on him than on her, so a guide with separate descriptions for her uses those when the shopper picked "For her". */
export function chainChoices(config, wearer) {
  const offered = config.sizes.map(size => size.label);
  const lengths = config.guide.lengths || [];
  const forHer = wearer === 'her' && config.guide.descriptionsHer?.length;
  const descriptions = (forHer ? config.guide.descriptionsHer : config.guide.descriptions) || [];
  const images = config.guide.images || [];
  // Badges are written as "length | text", e.g. 18" | Women's pick.
  const badges = Object.fromEntries((config.guide.badges || []).map(entry => entry.split('|').map(part => part.trim())));
  return lengths
    .map((size, i) => ({ size, label: descriptions[i], image: images[i], badge: badges[size] }))
    .filter(choice => choice.label && offered.includes(choice.size));
}

const create = (tag, className = '', text = '') => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

const RESULT_TTL = 86400000;

/* "We recommend size M" under the size option, shown after the customer used the finder. */
class SizeGuideNote extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.refresh = () => {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.dataset.key)); } catch {}
      const valid = saved && saved.signature === this.dataset.signature && Date.now() - saved.time < RESULT_TTL;
      this.hidden = !valid;
      this.replaceChildren();
      if (valid) {
        const [before, after = ''] = this.dataset.message.split('[size]');
        this.append(document.createTextNode(before), create('strong', '', saved.label), document.createTextNode(after));
      }
    };
    this.refresh();
    document.addEventListener('size-guide:result', this.refresh);
  }

  disconnectedCallback() {
    document.removeEventListener('size-guide:result', this.refresh);
  }
}

class SizeGuide extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.initialized) return;
    try { this.config = JSON.parse(this.querySelector('[data-size-guide-config]').textContent); } catch { return; }
    if (!['clothing', 'footwear', 'ring', 'bracelet', 'chain'].includes(this.config.mode)) return;

    this.initialized = true;
    this.labels = this.config.labels;
    this.config.guide ||= {};
    this.answers = { fit: 'regular' };
    this.answered = {};
    this.step = 0;
    this.stage = this.querySelector('[data-guide-stage]');
    this.status = this.querySelector('[data-guide-status]');
    this.steps = this.getSteps();
    this.abort = new AbortController();

    this.addEventListener('click', event => {
      const unit = event.target.closest('[data-guide-unit]');
      if (unit) {
        this.querySelectorAll('[data-guide-unit]').forEach(button => button.setAttribute('aria-pressed', String(button === unit)));
        this.querySelectorAll('[data-guide-chart] [data-unit]').forEach(value => { value.hidden = value.dataset.unit !== unit.dataset.guideUnit; });
        return;
      }
      const tab = event.target.closest('[data-guide-tab]');
      if (!tab) return;
      this.querySelector('[data-guide-finder]').hidden = tab.dataset.guideTab !== 'finder';
      this.querySelector('[data-guide-chart]').hidden = tab.dataset.guideTab !== 'chart';
      this.querySelectorAll('[data-guide-tab]').forEach(button => {
        const active = button === tab;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('button--outline', !active);
      });
    }, { signal: this.abort.signal });

    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.initialized = false;
  }

  getSteps() {
    const guide = this.config.guide;
    switch (this.config.mode) {
      case 'clothing': return ['usual', ...(guide.askHeight ? ['height'] : []), 'fit'];
      case 'footwear': return ['shoe', ...(guide.askWidth ? ['width'] : [])];
      case 'ring': return ['ring'];
      case 'bracelet': return ['wrist'];
      case 'chain': return [...(guide.descriptionsHer?.length ? ['wearer'] : []), 'choice'];
    }
    return [];
  }

  title(key) {
    if (key === 'choice') return this.config.question || this.labels.chain_fit;
    if (key === 'wearer') return this.labels.wearer;
    if (key === 'ring') return this.config.question || this.labels.ring;
    return this.labels[key];
  }

  button(text, action, className = 'button') {
    const button = create('button', className, text);
    button.type = 'button';
    button.addEventListener('click', action);
    return button;
  }

  next() {
    this.step++;
    this.render();
    this.stage.querySelector('input:not([type="range"]), button')?.focus({ preventScroll: true });
  }

  render() {
    this.stage.replaceChildren();
    this.status.textContent = '';
    this.renderProgress();

    const key = this.steps[this.step];
    if (!key) return this.showResult();

    this.stage.append(create('h3', 'h5 text-center', this.title(key)));
    switch (key) {
      case 'usual': this.renderChoices('usual', this.config.sizes.map(size => size.label)); break;
      case 'fit': this.renderChoices('fit', ['tight', 'regular', 'loose']); break;
      case 'height': this.renderHeight(); break;
      case 'shoe': this.renderShoe(); break;
      case 'width': this.renderWidth(); break;
      case 'ring': this.renderRing(); break;
      case 'wrist': this.renderWrist(); break;
      case 'wearer': this.renderWearer(); break;
      case 'choice': this.renderChain(); break;
    }

    if (this.step > 0) {
      this.stage.append(this.button(this.labels.back, () => { this.step--; this.render(); }, 'link justify-self-center'));
    }
  }

  renderProgress() {
    let progress = this.querySelector('[data-guide-progress]');
    if (!progress) {
      progress = create('p', 'text-sm text-subdued text-center');
      progress.dataset.guideProgress = '';
      this.stage.after(progress);
    }
    progress.hidden = this.steps.length < 2 || this.step >= this.steps.length;
    progress.textContent = this.labels.step.replace('{current}', this.step + 1).replace('{total}', this.steps.length);
  }

  renderChoices(key, choices) {
    // Sizes are swatches like the product page size picker; word answers are outline buttons, stacked.
    const words = key === 'fit';
    const group = create('div', 'size-guide__choices' + (words ? ' size-guide__choices--stack' : ''));
    for (const choice of choices) {
      const text = words ? this.labels[`${choice}_fit`] : choice;
      const className = words ? 'button button--outline size-guide__choice' : 'block-swatch size-guide__choice';
      const button = this.button(text, () => { this.answers[key] = choice; this.answered[key] = true; this.next(); }, className);
      button.setAttribute('aria-pressed', String(Boolean(this.answered[key]) && this.answers[key] === choice));
      group.append(button);
    }
    this.stage.append(group);
  }

  renderHeight() {
    const min = 120, max = 220;
    this.answers.height ??= 170;
    const id = `${this.closest('x-modal').id}-height`;
    const output = create('output', 'h3 text-center');
    output.htmlFor = id;
    const metric = create('span', 'text-subdued text-center');
    const slider = create('input', 'range size-guide__range');
    Object.assign(slider, { id, type: 'range', min, max, step: 1, value: this.answers.height });
    slider.setAttribute('aria-label', this.labels.height);
    const sync = () => {
      this.answers.height = Number(slider.value);
      const inches = Math.round(this.answers.height / 2.54);
      output.textContent = `${Math.floor(inches / 12)} ft ${inches % 12} in`;
      metric.textContent = `${this.answers.height} cm`;
      slider.setAttribute('aria-valuetext', `${output.textContent}, ${metric.textContent}`);
      slider.style.setProperty('--size-guide-progress', `${(this.answers.height - min) / (max - min) * 100}%`);
    };
    slider.addEventListener('input', sync);
    sync();
    this.stage.append(metric, output, slider, this.button(this.labels.next, () => this.next()));
  }

  renderShoe() {
    const guide = this.config.guide;
    const systems = guide.systems?.length ? guide.systems : [guide.system];
    const unit = this.shoeUnit || systems[0];
    const tabs = create('div', 'h-stack justify-center gap-2');
    for (const system of systems) {
      const tab = this.button(system.toUpperCase(), () => { this.shoeUnit = system; this.render(); }, 'button button--sm button--outline size-guide__unit');
      tab.setAttribute('aria-pressed', String(unit === system));
      tabs.append(tab);
    }
    const choices = create('div', 'size-guide__choices');
    const offered = new Set(this.config.sizes.map(size => normalizeSize(size.label)));
    for (const row of guide.table || []) {
      if (row[unit] === undefined || !offered.has(normalizeSize(row[guide.system]))) continue;
      const choice = this.button(String(row[unit]), () => {
        this.answers.shoe = String(row[unit]);
        this.answers.shoeSystem = unit;
        this.next();
      }, 'block-swatch size-guide__choice');
      choice.setAttribute('aria-label', `${unit.toUpperCase()} ${row[unit]}`);
      choices.append(choice);
    }
    this.stage.append(tabs, choices);
  }

  renderWidth() {
    const group = create('div', 'size-guide__choices');
    for (const width of ['narrow', 'regular', 'wide']) {
      const button = this.button('', () => { this.answers.width = width; this.next(); }, 'block-swatch v-stack gap-3 size-guide__width-choice');
      const image = create('img');
      Object.assign(image, { src: this.config.guide.widthImages[width], alt: '', width: 90, height: 132 });
      button.append(image, create('span', '', this.labels[width]));
      group.append(button);
    }
    this.stage.append(group);
  }

  renderSlider({ id, label, min, max, value, format, onChange }) {
    const current = create('output', 'h4 text-center');
    current.htmlFor = id;
    const slider = create('input', 'range size-guide__range');
    Object.assign(slider, { id, type: 'range', min, max, step: 'any', value });
    slider.setAttribute('aria-label', label);
    slider.disabled = min === max;
    const sync = () => {
      onChange(Number(slider.value));
      current.textContent = format(Number(slider.value));
      slider.setAttribute('aria-valuetext', current.textContent);
      slider.style.setProperty('--size-guide-progress', `${max === min ? 100 : (slider.value - min) / (max - min) * 100}%`);
    };
    slider.addEventListener('input', sync);
    sync();
    const ends = create('div', 'size-guide__actions text-sm text-subdued');
    ends.append(create('span', '', format(min)), create('span', '', format(max)));
    const control = create('div', 'size-guide__slider-control');
    control.append(slider, ends);
    return [current, control];
  }

  unitTabs(units, active, onPick) {
    const tabs = create('div', 'h-stack justify-center gap-2');
    for (const unit of units) {
      const tab = this.button(this.labels[unit] || unit, () => onPick(unit), 'button button--sm button--outline size-guide__unit');
      tab.setAttribute('aria-pressed', String(unit === active));
      tabs.append(tab);
    }
    return tabs;
  }

  renderRing() {
    const guide = this.config.guide;
    const offered = new Set(this.config.sizes.map(size => size.label));
    const measurements = (guide.sizes || []).map((size, i) => offered.has(size) ? Number(guide.diameters[i]) : NaN).filter(value => value > 0);
    if (!measurements.length) { this.status.textContent = this.labels.unavailable; return; }

    this.ringMethod ||= 'circumference';
    this.ringUnit ||= 'cm';
    const factor = (this.ringMethod === 'diameter' ? 1 : Math.PI) / (this.ringUnit === 'cm' ? 10 : 25.4);
    const format = diameter => `${Number((diameter * factor).toFixed(2))} ${this.ringUnit}`;
    const min = Math.min(...measurements), max = Math.max(...measurements);
    this.answers.ring ??= measurements[Math.floor(measurements.length / 2)];

    this.stage.append(
      this.unitTabs(['circumference', 'diameter'], this.ringMethod, method => { this.ringMethod = method; this.render(); }),
      this.unitTabs(['in', 'cm'], this.ringUnit, unit => { this.ringUnit = unit; this.render(); }),
      create('p', 'text-subdued text-center', this.labels[`${this.ringMethod}_help`]),
      ...this.renderSlider({
        id: `${this.closest('x-modal').id}-ring`,
        label: `${this.labels[this.ringMethod]} (${this.ringUnit})`,
        min, max, value: Math.min(max, Math.max(min, this.answers.ring)), format,
        onChange: value => { this.answers.ring = value; }
      }),
      this.button(this.labels.find, () => this.next())
    );
  }

  renderWrist() {
    const lengths = this.config.sizes.map(size => lengthCm(size.label)).filter(value => value !== null);
    if (!lengths.length) { this.status.textContent = this.labels.unavailable; return; }

    const inches = this.config.sizes.every(size => /(in|inch|inches|["″])$/i.test(String(size.label).trim()));
    this.wristUnit ||= inches ? 'in' : 'cm';
    const factor = this.wristUnit === 'in' ? 2.54 : 1;
    const min = Math.min(...lengths) - 2, max = Math.max(...lengths);
    this.answers.wrist ??= (min + max) / 2;

    this.stage.append(
      this.unitTabs(['in', 'cm'], this.wristUnit, unit => { this.wristUnit = unit; this.render(); }),
      create('p', 'text-subdued text-center', this.labels.measure_wrist),
      ...this.renderSlider({
        id: `${this.closest('x-modal').id}-wrist`,
        label: `${this.labels.wrist} (${this.wristUnit})`,
        min, max, value: this.answers.wrist,
        format: cm => `${Number((cm / factor).toFixed(1))} ${this.wristUnit}`,
        onChange: value => { this.answers.wrist = value; }
      }),
      this.button(this.labels.next, () => this.next())
    );
  }

  renderWearer() {
    const group = create('div', 'size-guide__choices size-guide__choices--stack');
    for (const wearer of ['him', 'her']) {
      const button = this.button(this.labels[`for_${wearer}`], () => { this.answers.wearer = wearer; this.next(); }, 'button button--outline size-guide__choice');
      button.setAttribute('aria-pressed', String(this.answers.wearer === wearer));
      group.append(button);
    }
    this.stage.append(group);
  }

  renderChain() {
    const choices = chainChoices(this.config, this.answers.wearer);
    if (!choices.length) {
      this.renderChoices('choice', this.config.sizes.map(size => size.label));
      return;
    }
    const group = create('div', 'size-guide__choices size-guide__choices--stack');
    for (const choice of choices) {
      const button = this.button('', () => { this.answers.choice = choice.size; this.next(); }, `button button--outline size-guide__choice${choice.image ? ' v-stack gap-2' : ''}`);
      button.setAttribute('aria-pressed', String(this.answers.choice === choice.size));
      if (choice.image) {
        const image = create('img', 'size-guide__choice-image');
        Object.assign(image, { src: choice.image, alt: '', loading: 'lazy' });
        button.append(image);
      }
      button.append(create('span', '', choice.label));
      if (choice.badge) button.append(create('span', 'badge badge--custom size-guide__badge', choice.badge));
      group.append(button);
    }
    this.stage.append(group);
  }

  currentVariant(label) {
    const picker = [...document.querySelectorAll('variant-picker')].find(p => p.getAttribute('form-id') === this.dataset.formId);
    const current = picker?.selectedVariant;
    if (!current) return null;
    const position = Number(this.dataset.optionPosition) - 1;
    return this.config.variants.find(v => v.options.every((value, i) => value === (i === position ? label : current.options[i])));
  }

  saveResult(label) {
    try {
      if (label) sessionStorage.setItem(this.dataset.resultKey, JSON.stringify({ label, signature: this.dataset.signature, time: Date.now() }));
      else sessionStorage.removeItem(this.dataset.resultKey);
    } catch {}
    document.dispatchEvent(new CustomEvent('size-guide:result'));
  }

  showResult() {
    const result = recommend(this.config, this.answers);
    this.saveResult(result.label);

    if (!result.label) {
      this.status.textContent = this.labels.outside;
    } else {
      const variant = this.currentVariant(result.label);
      const select = this.button(this.labels.select, () => this.apply(result.label));
      select.disabled = !variant?.available;
      if (!variant?.available) this.status.textContent = this.labels[variant ? 'sold_out' : 'unavailable'];
      this.stage.classList.add('size-guide__result');
      this.stage.append(create('p', 'text-base', this.config.resultMessage || this.labels.result), create('p', 'size-guide__size', result.label), select);
    }

    this.stage.append(this.button(this.labels.edit, () => {
      this.step = 0;
      this.stage.classList.remove('size-guide__result');
      this.render();
    }, 'link justify-self-center'));
  }

  async apply(label) {
    const form = document.getElementById(this.dataset.formId);
    const picker = [...document.querySelectorAll('variant-picker')].find(p => p.getAttribute('form-id') === this.dataset.formId);
    const target = this.config.sizes.find(size => size.label === label);
    if (!form || !picker || !target?.id || !this.currentVariant(label)?.available) {
      this.status.textContent = this.labels.unavailable;
      return;
    }

    const position = Number(this.dataset.optionPosition);
    const checked = [...form.elements]
      .filter(input => input.matches('input[data-option-position]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    const values = checked.map(input => Number(input.dataset.optionPosition) === position ? String(target.id) : input.value);
    if (values.length !== picker.selectedVariant.options.length) {
      this.status.textContent = this.labels.error;
      return;
    }

    const buttons = this.stage.querySelectorAll('button');
    buttons.forEach(button => { button.disabled = true; });
    try {
      await picker.selectCombination({ optionValues: values, productChange: false });
      await this.closest('x-modal').hide();
    } catch {
      this.status.textContent = this.labels.error;
      buttons.forEach(button => { button.disabled = false; });
    }
  }
}

if (globalThis.customElements && !customElements.get('size-guide')) customElements.define('size-guide', SizeGuide);
if (globalThis.customElements && !customElements.get('size-guide-note')) customElements.define('size-guide-note', SizeGuideNote);
